import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import logoUrl from "./logo.svg";
import { I18nProvider, useI18n, LanguageSwitcher } from "./i18n";
import { truncateFilename, formatBytes } from "./utils";

// ── Types ──

interface CustomerForm {
  id?: string;
  type: "BUSINESS";
  email: string;
  tax_id: string;
  country: string;
  phone: string;
  name: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  birth_date: string;
  professional_situation: string;
  professional_occupation: string;
  legal_name: string;
  incorporation_date: string;
  company_type: string;
  annual_revenue: string;
  monthly_transaction_volume: string;
  compliance_and_aml: string;
  is_regulated_activity: boolean;
  regulated_activity_details: string;
  website: string;
  no_website_reason: string;
  services_provided: string;
  source_of_funds: string;
  monthly_volume_usd: string;
  transaction_volume_usd: string;
  jurisdictions: string[];
  involved_activities: string[];
  transaction_counterparties: string[];
  address_country: string;
  address_line1: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  invoice_type: string;
  status: string;
  revision?: number;
  created_at?: string;
  updated_at?: string;
  admin_notes?: string;
}

interface AssociatedParty {
  id?: string;
  _localId: string;
  customer_id?: string;
  type: "INDIVIDUAL" | "BUSINESS";
  role: "UBO" | "SHAREHOLDER" | "REPRESENTATIVE";
  name: string;
  birth_date: string;
  email: string;
  tax_id: string;
  title_occupation: string;
  legal_name: string;
  incorporation_date: string;
  ownership_percentage: number | "";
  address_country: string;
  address_line1: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  id_doc_country: string;
  id_doc_type: string;
  id_doc_single_side: boolean;
  proof_address_type: string;
}

interface DocEntry {
  id: string;
  type: string;
  side: string | null;
  file_name: string;
  file_size: number;
  partyLocalId: string | null;
}

// ── Constants ──

const EMPTY_CUSTOMER: CustomerForm = {
  type: "BUSINESS",
  email: "", tax_id: "", country: "BRA", phone: "",
  name: "", first_name: "", middle_name: "", last_name: "", birth_date: "",
  professional_situation: "", professional_occupation: "",
  legal_name: "", incorporation_date: "", company_type: "",
  annual_revenue: "", monthly_transaction_volume: "",
  compliance_and_aml: "", is_regulated_activity: false, regulated_activity_details: "",
  website: "", no_website_reason: "", services_provided: "",
  source_of_funds: "", monthly_volume_usd: "", transaction_volume_usd: "",
  jurisdictions: [], involved_activities: [], transaction_counterparties: [],
  address_country: "BRA", address_line1: "", address_city: "", address_state: "", address_postal_code: "",
  invoice_type: "",
  status: "DRAFT",
};

function makeLocalId() {
  return crypto.randomUUID().replace(/-/g, "");
}

function makeParty(): AssociatedParty {
  return {
    _localId: makeLocalId(),
    type: "INDIVIDUAL", role: "UBO",
    name: "", birth_date: "", email: "", tax_id: "", title_occupation: "",
    legal_name: "", incorporation_date: "",
    ownership_percentage: "",
    address_country: "BRA", address_line1: "", address_city: "", address_state: "", address_postal_code: "",
    id_doc_country: "BRA", id_doc_type: "ID_CARD", id_doc_single_side: false, proof_address_type: "BANK_STATEMENT",
  };
}

const COMPANY_TYPES = [
  // Trade / Import / Export related (top)
  "WHOLESALE_OF_DURABLE_GOODS", "WHOLESALE_OF_NON_DURABLE_GOODS",
  "RETAILER_OF_DURABLE_GOODS", "RETAILER_OF_NON_DURABLE_GOODS",
  "CONSTRUCTION_OR_SKILLED_TRADE_BUSINESSES", "COMMODITIES_FIRMS", "FOREIGN_EXCHANGE_MARKET_FX",
  // Other
  "BLOCKCHAIN_SOFTWARE_COMPANY", "BANK_US", "BANK_FOREIGN", "NFT_MARKETPLACE",
  "THIRD_PARTY_PAYMENT_PROCESSOR", "ATM_KIOSK_OPERATOR", "BROKER_DEALER_OTC_DESK",
  "CHARITY_NGO_NON_PROFIT", "DEFI_EXCHANGE",
  "GOVERNMENT_AGENCY_FOREIGN", "GOVERNMENT_AGENCY_US",
  "PAYMENT_GATEWAY", "BANKING_CORRESPONDENT", "HIGH_NET_WORTH_INDIVIDUALS",
  "INSURANCE_COMPANIES", "INVESTMENT_ADVISOR_ASSET_MANAGER", "LOAN_OR_FINANCE_COMPANY",
  "MINER", "NON_BANK_CUSTODIANS", "PENSION_FUNDS", "WEALTH_HOLDING_VEHICLE",
  "POOLED_INVESTMENT_VEHICLE_FUND", "SPECIAL_PURPOSE_VEHICLE", "SUPRANATIONAL_BODIES",
  "TOKEN_PROJECT", "TRUST_PERSONAL", "TRUST_CORPORATE",
  "U_S_CRYPTO_MSB", "FOREIGN_CRYPTO_MSB", "U_S_NON_CRYPTO_MSB", "FOREIGN_NON_CRYPTO_MSB",
  "PROFESSIONAL_SERVICES", "OTHER",
];

const SOURCE_OF_FUNDS_BUSINESS = [
  "COMPANY", "COMPANY_CAPITAL", "INVESTMENT", "LOAN", "PRIVATE_CAPITAL", "GRANT", "OTHER",
];
const COUNTRIES: SelectOption[] = [
  { value: "AFG", label: "Afghanistan" }, { value: "ALB", label: "Albania" }, { value: "DZA", label: "Algeria" },
  { value: "AND", label: "Andorra" }, { value: "AGO", label: "Angola" }, { value: "ATG", label: "Antigua and Barbuda" },
  { value: "ARG", label: "Argentina" }, { value: "ARM", label: "Armenia" }, { value: "AUS", label: "Australia" },
  { value: "AUT", label: "Austria" }, { value: "AZE", label: "Azerbaijan" }, { value: "BHS", label: "Bahamas" },
  { value: "BHR", label: "Bahrain" }, { value: "BGD", label: "Bangladesh" }, { value: "BRB", label: "Barbados" },
  { value: "BLR", label: "Belarus" }, { value: "BEL", label: "Belgium" }, { value: "BLZ", label: "Belize" },
  { value: "BEN", label: "Benin" }, { value: "BTN", label: "Bhutan" }, { value: "BOL", label: "Bolivia" },
  { value: "BIH", label: "Bosnia and Herzegovina" }, { value: "BWA", label: "Botswana" }, { value: "BRA", label: "Brazil" },
  { value: "BRN", label: "Brunei" }, { value: "BGR", label: "Bulgaria" }, { value: "BFA", label: "Burkina Faso" },
  { value: "BDI", label: "Burundi" }, { value: "CPV", label: "Cabo Verde" }, { value: "KHM", label: "Cambodia" },
  { value: "CMR", label: "Cameroon" }, { value: "CAN", label: "Canada" }, { value: "CAF", label: "Central African Republic" },
  { value: "TCD", label: "Chad" }, { value: "CHL", label: "Chile" }, { value: "CHN", label: "China" },
  { value: "COL", label: "Colombia" }, { value: "COM", label: "Comoros" }, { value: "COG", label: "Congo" },
  { value: "COD", label: "Congo (DRC)" }, { value: "CRI", label: "Costa Rica" }, { value: "CIV", label: "Cote d'Ivoire" },
  { value: "HRV", label: "Croatia" }, { value: "CUB", label: "Cuba" }, { value: "CYP", label: "Cyprus" },
  { value: "CZE", label: "Czech Republic" }, { value: "DNK", label: "Denmark" }, { value: "DJI", label: "Djibouti" },
  { value: "DMA", label: "Dominica" }, { value: "DOM", label: "Dominican Republic" }, { value: "ECU", label: "Ecuador" },
  { value: "EGY", label: "Egypt" }, { value: "SLV", label: "El Salvador" }, { value: "GNQ", label: "Equatorial Guinea" },
  { value: "ERI", label: "Eritrea" }, { value: "EST", label: "Estonia" }, { value: "SWZ", label: "Eswatini" },
  { value: "ETH", label: "Ethiopia" }, { value: "FJI", label: "Fiji" }, { value: "FIN", label: "Finland" },
  { value: "FRA", label: "France" }, { value: "GAB", label: "Gabon" }, { value: "GMB", label: "Gambia" },
  { value: "GEO", label: "Georgia" }, { value: "DEU", label: "Germany" }, { value: "GHA", label: "Ghana" },
  { value: "GRC", label: "Greece" }, { value: "GRD", label: "Grenada" }, { value: "GTM", label: "Guatemala" },
  { value: "GIN", label: "Guinea" }, { value: "GNB", label: "Guinea-Bissau" }, { value: "GUY", label: "Guyana" },
  { value: "HTI", label: "Haiti" }, { value: "HND", label: "Honduras" }, { value: "HUN", label: "Hungary" },
  { value: "ISL", label: "Iceland" }, { value: "IND", label: "India" }, { value: "IDN", label: "Indonesia" },
  { value: "IRN", label: "Iran" }, { value: "IRQ", label: "Iraq" }, { value: "IRL", label: "Ireland" },
  { value: "ISR", label: "Israel" }, { value: "ITA", label: "Italy" }, { value: "JAM", label: "Jamaica" },
  { value: "JPN", label: "Japan" }, { value: "JOR", label: "Jordan" }, { value: "KAZ", label: "Kazakhstan" },
  { value: "KEN", label: "Kenya" }, { value: "KIR", label: "Kiribati" }, { value: "PRK", label: "Korea (North)" },
  { value: "KOR", label: "Korea (South)" }, { value: "KWT", label: "Kuwait" }, { value: "KGZ", label: "Kyrgyzstan" },
  { value: "LAO", label: "Laos" }, { value: "LVA", label: "Latvia" }, { value: "LBN", label: "Lebanon" },
  { value: "LSO", label: "Lesotho" }, { value: "LBR", label: "Liberia" }, { value: "LBY", label: "Libya" },
  { value: "LIE", label: "Liechtenstein" }, { value: "LTU", label: "Lithuania" }, { value: "LUX", label: "Luxembourg" },
  { value: "MDG", label: "Madagascar" }, { value: "MWI", label: "Malawi" }, { value: "MYS", label: "Malaysia" },
  { value: "MDV", label: "Maldives" }, { value: "MLI", label: "Mali" }, { value: "MLT", label: "Malta" },
  { value: "MHL", label: "Marshall Islands" }, { value: "MRT", label: "Mauritania" }, { value: "MUS", label: "Mauritius" },
  { value: "MEX", label: "Mexico" }, { value: "FSM", label: "Micronesia" }, { value: "MDA", label: "Moldova" },
  { value: "MCO", label: "Monaco" }, { value: "MNG", label: "Mongolia" }, { value: "MNE", label: "Montenegro" },
  { value: "MAR", label: "Morocco" }, { value: "MOZ", label: "Mozambique" }, { value: "MMR", label: "Myanmar" },
  { value: "NAM", label: "Namibia" }, { value: "NRU", label: "Nauru" }, { value: "NPL", label: "Nepal" },
  { value: "NLD", label: "Netherlands" }, { value: "NZL", label: "New Zealand" }, { value: "NIC", label: "Nicaragua" },
  { value: "NER", label: "Niger" }, { value: "NGA", label: "Nigeria" }, { value: "MKD", label: "North Macedonia" },
  { value: "NOR", label: "Norway" }, { value: "OMN", label: "Oman" }, { value: "PAK", label: "Pakistan" },
  { value: "PLW", label: "Palau" }, { value: "PAN", label: "Panama" }, { value: "PNG", label: "Papua New Guinea" },
  { value: "PRY", label: "Paraguay" }, { value: "PER", label: "Peru" }, { value: "PHL", label: "Philippines" },
  { value: "POL", label: "Poland" }, { value: "PRT", label: "Portugal" }, { value: "QAT", label: "Qatar" },
  { value: "ROU", label: "Romania" }, { value: "RUS", label: "Russia" }, { value: "RWA", label: "Rwanda" },
  { value: "KNA", label: "Saint Kitts and Nevis" }, { value: "LCA", label: "Saint Lucia" },
  { value: "VCT", label: "Saint Vincent and the Grenadines" }, { value: "WSM", label: "Samoa" },
  { value: "SMR", label: "San Marino" }, { value: "STP", label: "Sao Tome and Principe" },
  { value: "SAU", label: "Saudi Arabia" }, { value: "SEN", label: "Senegal" }, { value: "SRB", label: "Serbia" },
  { value: "SYC", label: "Seychelles" }, { value: "SLE", label: "Sierra Leone" }, { value: "SGP", label: "Singapore" },
  { value: "SVK", label: "Slovakia" }, { value: "SVN", label: "Slovenia" }, { value: "SLB", label: "Solomon Islands" },
  { value: "SOM", label: "Somalia" }, { value: "ZAF", label: "South Africa" }, { value: "SSD", label: "South Sudan" },
  { value: "ESP", label: "Spain" }, { value: "LKA", label: "Sri Lanka" }, { value: "SDN", label: "Sudan" },
  { value: "SUR", label: "Suriname" }, { value: "SWE", label: "Sweden" }, { value: "CHE", label: "Switzerland" },
  { value: "SYR", label: "Syria" }, { value: "TWN", label: "Taiwan" }, { value: "TJK", label: "Tajikistan" },
  { value: "TZA", label: "Tanzania" }, { value: "THA", label: "Thailand" }, { value: "TLS", label: "Timor-Leste" },
  { value: "TGO", label: "Togo" }, { value: "TON", label: "Tonga" }, { value: "TTO", label: "Trinidad and Tobago" },
  { value: "TUN", label: "Tunisia" }, { value: "TUR", label: "Turkey" }, { value: "TKM", label: "Turkmenistan" },
  { value: "TUV", label: "Tuvalu" }, { value: "UGA", label: "Uganda" }, { value: "UKR", label: "Ukraine" },
  { value: "ARE", label: "United Arab Emirates" }, { value: "GBR", label: "United Kingdom" },
  { value: "USA", label: "United States" }, { value: "URY", label: "Uruguay" }, { value: "UZB", label: "Uzbekistan" },
  { value: "VUT", label: "Vanuatu" }, { value: "VAT", label: "Vatican City" }, { value: "VEN", label: "Venezuela" },
  { value: "VNM", label: "Vietnam" }, { value: "YEM", label: "Yemen" }, { value: "ZMB", label: "Zambia" },
  { value: "ZWE", label: "Zimbabwe" },
];

const JURISDICTIONS = ["BRA", "USA", "EU", "CANADA", "CHINA", "MEXICO", "OTHERS"];
const INVOLVED_ACTIVITIES = ["NONE", "ADULT_ENTERTAINMENT", "DRUGS", "FIREARMS", "GAMBLING", "MARIJUANA", "TUMBLING"];
const TRANSACTION_COUNTERPARTIES = ["SELF", "MERCHANTS_SUPPLIERS", "CUSTOMERS", "EMPLOYEES", "CONTRACTORS", "FRIENDS", "FAMILY"];

function useNoWebsiteReasons() {
  const { t } = useI18n();
  return [
    { value: "UNDER_DEVELOPMENT", label: t("underDevelopment") },
    { value: "NOT_APPLICABLE", label: t("notApplicable") },
    { value: "CONFIDENTIAL", label: t("confidential") },
    { value: "SOCIAL_MEDIA_ONLY", label: t("socialMediaOnly") },
  ];
}

function useIdDocTypes() {
  const { t } = useI18n();
  return [
    { value: "ID_CARD", label: t("idCard") },
    { value: "PASSPORT", label: t("passport") },
    { value: "DRIVERS_LICENSE", label: t("driversLicense") },
  ];
}

// ── Session ID from URL ──

function getSessionId(): string {
  const params = new URLSearchParams(window.location.search);
  const existing = params.get("session");
  if (existing && /^[0-9a-f-]{32,36}$/i.test(existing)) return existing.replace(/-/g, "");
  const newId = crypto.randomUUID().replace(/-/g, "");
  params.set("session", newId);
  window.history.replaceState(null, "", `?${params.toString()}`);
  return newId;
}

// ── Prefill from query param ──

const PREFILL_KEYS: (keyof CustomerForm)[] = [
  "legal_name", "country", "email", "tax_id", "phone", "website",
  "no_website_reason", "services_provided", "incorporation_date",
  "address_country", "address_line1", "address_city", "address_state", "address_postal_code",
];

function getPrefillData(): Partial<CustomerForm> | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("prefill");
    if (!raw) return null;
    let json: string;
    try { json = atob(raw); } catch { json = raw; }
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    const result: Partial<CustomerForm> = {};
    for (const key of PREFILL_KEYS) {
      if (key in parsed && typeof parsed[key] === "string") {
        (result as any)[key] = parsed[key];
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch { return null; }
}

// ── localStorage helpers ──

function lsKey(sessionId: string) { return `kyb_session_${sessionId}`; }

function loadSession(sessionId: string): { form: CustomerForm; parties: AssociatedParty[]; documents: DocEntry[] } | null {
  try {
    const raw = localStorage.getItem(lsKey(sessionId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveSessionToLS(sessionId: string, form: CustomerForm, parties: AssociatedParty[], documents: DocEntry[]) {
  localStorage.setItem(lsKey(sessionId), JSON.stringify({ form, parties, documents }));
}

function autosaveSnapshot(form: CustomerForm, parties: AssociatedParty[]) {
  const editableForm: Record<string, unknown> = { ...form };
  for (const key of ["id", "status", "revision", "created_at", "updated_at", "admin_notes"]) {
    delete editableForm[key];
  }
  const editableParties = parties.map(p => {
    const editableParty: Record<string, unknown> = { ...p };
    delete editableParty.id;
    delete editableParty.customer_id;
    return editableParty;
  });
  return JSON.stringify({ form: editableForm, parties: editableParties });
}

// ── Components ──

function Field({ label, required, children, fullWidth, fieldId }: { label: string; required?: boolean; children: React.ReactNode; fullWidth?: boolean; fieldId?: string }) {
  return (
    <div className={`field${fullWidth ? " full-width" : ""}`} data-field={fieldId}>
      <label>{label}{required && <span className="required">*</span>}</label>
      {children}
    </div>
  );
}

function CheckboxGroup({ options, value, onChange }: { options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="checkbox-group">
      {options.map(opt => (
        <label key={opt}>
          <input
            type="checkbox"
            checked={value.includes(opt)}
            onChange={e => {
              if (e.target.checked) onChange([...value, opt]);
              else onChange(value.filter(v => v !== opt));
            }}
          />
          {opt.replace(/_/g, " ")}
        </label>
      ))}
    </div>
  );
}

type SelectOption = { value: string; label: string };
function CustomSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  className,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[] | string[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const opts: SelectOption[] = Array.isArray(options) && options.length > 0
    ? typeof options[0] === "string"
      ? (options as string[]).map(s => ({ value: s, label: s.replace(/_/g, " ") }))
      : (options as SelectOption[])
    : [];

  useEffect(() => {
    if (!open) return;
    const listEl = listRef.current;
    if (listEl) listEl.scrollTop = 0;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  const selected = opts.find(o => o.value === value);
  const display = selected ? selected.label : placeholder;

  return (
    <div className={`custom-select-wrap ${className ?? ""}`.trim()} ref={containerRef} style={style}>
      <button
        type="button"
        className="custom-select-trigger"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={!value ? "placeholder" : ""}>{display}</span>
        <svg className="custom-select-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 4l4 4 4-4" /></svg>
      </button>
      {open && (
        <div className="custom-select-dropdown" role="listbox">
          <div className="custom-select-list" ref={listRef}>
            {opts.map(o => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={value === o.value}
                className={`custom-select-option${value === o.value ? " selected" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dedicated Upload Slot ──

function UploadSlot({ label, description, sessionId, docType, side, partyLocalId, customerId, documents, onUpload, onDelete, disabled }: {
  label: string;
  description?: string;
  sessionId: string;
  docType: string;
  side?: string;
  partyLocalId?: string;
  customerId?: string;
  documents: DocEntry[];
  onUpload: (doc: DocEntry) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const existingDoc = documents.find(d =>
    d.type === docType &&
    (side !== undefined ? d.side === side : (d.side == null || d.side === "")) &&
    (partyLocalId ? d.partyLocalId === partyLocalId : !d.partyLocalId)
  );

  const handleUpload = async (file: File) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", docType);
    fd.append("sessionId", sessionId);
    if (side) fd.append("side", side);
    if (partyLocalId) fd.append("partyLocalId", partyLocalId);
    if (customerId) fd.append("customerId", customerId);

    try {
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = (await res.json()) as { id: string };
      onUpload({
        id: data.id,
        type: docType,
        side: side || null,
        file_name: file.name,
        file_size: file.size,
        partyLocalId: partyLocalId || null,
      });
    } catch (err: unknown) {
      console.error(err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="upload-slot">
      {label && <div className="upload-slot-label">{label}</div>}
      {description && <div className="upload-slot-desc">{description}</div>}
      {existingDoc ? (
        <div className="uploaded-file">
          <div className="file-info">
            <span className="file-check">&#10003;</span>
            <span className="file-name" title={existingDoc.file_name}>{truncateFilename(existingDoc.file_name)}</span>
            <span style={{ color: "#9ca3af", fontSize: 11 }}>{formatBytes(existingDoc.file_size)}</span>
          </div>
          <button className="remove-btn" onClick={() => onDelete(existingDoc.id)} title="Remove">&times;</button>
        </div>
      ) : (
        <div
          className={`upload-area${uploading ? " uploading" : ""}${disabled ? " disabled" : ""}`}
          onClick={() => { if (!disabled) fileRef.current?.click(); }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { if (!disabled) handleDrop(e); }}
        >
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" disabled={disabled} onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
          <div className="upload-icon">&#8593;</div>
          <UploadText uploading={uploading} />
        </div>
      )}
    </div>
  );
}

function MultiUploadSlot({ label, description, sessionId, docType, partyLocalId, customerId, documents, onUpload, onDelete }: {
  label: string;
  description?: string;
  sessionId: string;
  docType: string;
  partyLocalId?: string;
  customerId?: string;
  documents: DocEntry[];
  onUpload: (doc: DocEntry) => void;
  onDelete: (id: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const existingDocs = documents.filter(d =>
    d.type === docType &&
    (d.side == null || d.side === "") &&
    (partyLocalId ? d.partyLocalId === partyLocalId : !d.partyLocalId)
  );

  const handleUpload = async (file: File) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", docType);
    fd.append("sessionId", sessionId);
    if (partyLocalId) fd.append("partyLocalId", partyLocalId);
    if (customerId) fd.append("customerId", customerId);

    try {
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = (await res.json()) as { id: string };
      onUpload({
        id: data.id,
        type: docType,
        side: null,
        file_name: file.name,
        file_size: file.size,
        partyLocalId: partyLocalId || null,
      });
    } catch (err: unknown) {
      console.error(err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="upload-slot">
      {label && <div className="upload-slot-label">{label}</div>}
      {description && <div className="upload-slot-desc">{description}</div>}
      {existingDocs.map(doc => (
        <div key={doc.id} className="uploaded-file">
          <div className="file-info">
            <span className="file-check">&#10003;</span>
            <span className="file-name" title={doc.file_name}>{truncateFilename(doc.file_name)}</span>
            <span style={{ color: "#9ca3af", fontSize: 11 }}>{formatBytes(doc.file_size)}</span>
          </div>
          <button className="remove-btn" onClick={() => onDelete(doc.id)} title="Remove">&times;</button>
        </div>
      ))}
      <div
        className={`upload-area${uploading ? " uploading" : ""}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
        <div className="upload-icon">&#8593;</div>
        <UploadText uploading={uploading} />
      </div>
    </div>
  );
}

function UploadText({ uploading }: { uploading: boolean }) {
  const { t } = useI18n();
  return (<>
    <div className="upload-text">
      {uploading ? t("uploading") : <><strong>{t("clickToUpload")}</strong> {t("orDragToUpload")}</>}
    </div>
    <div className="upload-hint">{t("uploadHint")}</div>
  </>);
}

// ── Generic multi-doc upload (for optional docs) ──

function MultiUploadSlot({ label, description, sessionId, docType, partyLocalId, customerId, documents, onUpload, onDelete }: {
  label: string;
  description?: string;
  sessionId: string;
  docType: string;
  partyLocalId?: string;
  customerId?: string;
  documents: DocEntry[];
  onUpload: (doc: DocEntry) => void;
  onDelete: (id: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const matchingDocs = documents.filter(d =>
    d.type === docType &&
    (partyLocalId ? d.partyLocalId === partyLocalId : !d.partyLocalId)
  );

  const handleUpload = async (file: File) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", docType);
    fd.append("sessionId", sessionId);
    if (partyLocalId) fd.append("partyLocalId", partyLocalId);
    if (customerId) fd.append("customerId", customerId);

    try {
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = (await res.json()) as { id: string };
      onUpload({
        id: data.id,
        type: docType,
        side: null,
        file_name: file.name,
        file_size: file.size,
        partyLocalId: partyLocalId || null,
      });
    } catch (err) {
      console.error(err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="upload-slot">
      {label && <div className="upload-slot-label">{label}</div>}
      {description && <div className="upload-slot-desc">{description}</div>}
      {matchingDocs.map(doc => (
        <div key={doc.id} className="uploaded-file">
          <div className="file-info">
            <span className="file-check">&#10003;</span>
            <span className="file-name" title={doc.file_name}>{truncateFilename(doc.file_name)}</span>
            <span style={{ color: "#9ca3af", fontSize: 11 }}>{formatBytes(doc.file_size)}</span>
          </div>
          <button className="remove-btn" onClick={() => onDelete(doc.id)} title="Remove">&times;</button>
        </div>
      ))}
      <div
        className={`upload-area${uploading ? " uploading" : ""}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleUpload(f); }}
      >
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
        <UploadText uploading={uploading} />
      </div>
    </div>
  );
}

// ── Section wrapper (two-column) ──

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="form-section">
      <div className="section-label">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="section-content">
        {children}
      </div>
    </div>
  );
}

// ── Owner card ──

function OwnerCard({ party, index, onChange, onRemove, sessionId, documents, onDocUpload, onDocDelete, customerId }: {
  party: AssociatedParty;
  index: number;
  onChange: (p: AssociatedParty) => void;
  onRemove: () => void;
  sessionId: string;
  documents: DocEntry[];
  onDocUpload: (d: DocEntry) => void;
  onDocDelete: (id: string) => void;
  customerId?: string;
}) {
  const { t } = useI18n();
  const idDocTypes = useIdDocTypes();
  const set = (key: keyof AssociatedParty, val: any) => onChange({ ...party, [key]: val });
  const needsOwnership = party.role === "UBO" || party.role === "SHAREHOLDER";

  return (
    <div className="owner-card">
      <div className="owner-card-header">
        <h3>{t("ownerNum")} #{index + 1}</h3>
        <button className="btn btn-danger btn-sm" onClick={onRemove}>{t("remove")}</button>
      </div>

      <div className="form-grid">
        <Field label={t("role")} required fullWidth fieldId={`party_${party._localId}_role`}>
          <CustomSelect
            value={party.role}
            onChange={v => set("role", v)}
            options={[
              { value: "UBO", label: t("ubo") },
              { value: "SHAREHOLDER", label: t("shareholder") },
              { value: "REPRESENTATIVE", label: t("representative") },
            ]}
            placeholder={t("select")}
          />
        </Field>
        <Field label={t("fullName")} required fieldId={`party_${party._localId}_name`}>
          <input value={party.name} onChange={e => set("name", e.target.value)} placeholder={t("phFullName")} />
        </Field>
        <Field label={t("dateOfBirth")} required fieldId={`party_${party._localId}_birth_date`}>
          <input type="date" value={party.birth_date} onChange={e => set("birth_date", e.target.value)} />
        </Field>
        <Field label={t("titleOccupation")}>
          <input value={party.title_occupation} onChange={e => set("title_occupation", e.target.value)} placeholder={t("phOccupation")} />
        </Field>
        <Field label={t("email")} required fieldId={`party_${party._localId}_email`}>
          <input type="email" value={party.email} onChange={e => set("email", e.target.value)} placeholder={t("phOwnerEmail")} />
        </Field>
        <Field label={t("ownerTaxId")} required fieldId={`party_${party._localId}_tax_id`}>
          <input value={party.tax_id} onChange={e => set("tax_id", e.target.value)} placeholder={t("phOwnerTaxId")} />
        </Field>
        {needsOwnership && (
          <Field label={t("ownershipPct")} required fieldId={`party_${party._localId}_ownership`}>
            <input type="number" min="0" max="100" value={party.ownership_percentage} onChange={e => set("ownership_percentage", Number(e.target.value))} placeholder={t("phOwnership")} />
          </Field>
        )}
      </div>

      {/* Owner address */}
      <div className="owner-subsection">
        <h4>{t("address")}</h4>
        <div className="form-grid">
          <Field label={t("addressLine1")} required fieldId={`party_${party._localId}_address_line1`}>
            <input value={party.address_line1} onChange={e => set("address_line1", e.target.value)} placeholder={t("phOwnerAddress")} />
          </Field>
          <Field label={t("city")} required fieldId={`party_${party._localId}_address_city`}>
            <input value={party.address_city} onChange={e => set("address_city", e.target.value)} placeholder={t("phOwnerCity")} />
          </Field>
          <Field label={t("stateProvince")} required fieldId={`party_${party._localId}_address_state`}>
            <input value={party.address_state} onChange={e => set("address_state", e.target.value)} placeholder={t("phOwnerState")} />
          </Field>
          <Field label={t("postalCode")} required fieldId={`party_${party._localId}_address_postal_code`}>
            <input value={party.address_postal_code} onChange={e => set("address_postal_code", e.target.value)} placeholder={t("phPostalCode")} />
          </Field>
          <Field label={t("country")} required fieldId={`party_${party._localId}_address_country`}>
            <CustomSelect value={party.address_country} onChange={v => set("address_country", v)} options={COUNTRIES} placeholder={t("selectCountry")} />
          </Field>
        </div>
      </div>

      {/* Owner ID Document */}
      <div className="owner-subsection">
        <h4>{t("idDocument")} <span className="required">*</span></h4>
        <div className="form-grid" style={{ marginBottom: 16 }}>
          <Field label={t("countryOfIssue")}>
            <CustomSelect value={party.id_doc_country} onChange={v => set("id_doc_country", v)} options={COUNTRIES} placeholder={t("selectCountry")} />
          </Field>
          <Field label={t("documentType")} fullWidth>
            <CustomSelect value={party.id_doc_type} onChange={v => set("id_doc_type", v)} options={idDocTypes} />
          </Field>
        </div>
        <div className="id-doc-single-side-wrap" style={{ marginBottom: 16 }}>
          <label className="switch-label">
            <input
              type="checkbox"
              checked={!!party.id_doc_single_side}
              onChange={e => set("id_doc_single_side", e.target.checked)}
              className="switch-input"
            />
            <span className="switch-slider" />
            <span className="switch-text">{t("idDocSingleSide")}</span>
          </label>
        </div>
        <div className="form-grid" data-field={`party_${party._localId}_id_doc`}>
          {party.id_doc_single_side ? (
            <div className="full-width">
              <UploadSlot
                label={t("idDocument")}
                sessionId={sessionId}
                docType={party.id_doc_type}
                partyLocalId={party._localId}
                customerId={customerId}
                documents={documents}
                onUpload={onDocUpload}
                onDelete={onDocDelete}
              />
            </div>
          ) : (
            <>
              <UploadSlot
                label={t("documentFront")}
                sessionId={sessionId}
                docType={party.id_doc_type}
                side="FRONT_SIDE"
                partyLocalId={party._localId}
                customerId={customerId}
                documents={documents}
                onUpload={onDocUpload}
                onDelete={onDocDelete}
              />
              <UploadSlot
                label={t("documentBack")}
                sessionId={sessionId}
                docType={party.id_doc_type}
                side="BACK_SIDE"
                partyLocalId={party._localId}
                customerId={customerId}
                documents={documents}
                onUpload={onDocUpload}
                onDelete={onDocDelete}
              />
            </>
          )}
        </div>
      </div>

      {/* Owner Proof of Address */}
      <div className="owner-subsection" data-field={`party_${party._localId}_proof_address`}>
        <h4>{t("proofOfAddress")} <span className="required">*</span></h4>
        <UploadSlot
          label={t("document")}
          sessionId={sessionId}
          docType="BANK_STATEMENT"
          partyLocalId={party._localId}
          customerId={customerId}
          documents={documents}
          onUpload={onDocUpload}
          onDelete={onDocDelete}
        />
      </div>

      {/* Owner Bank Account Statement */}
      <div className="owner-subsection" data-field={`party_${party._localId}_bank_account_statement`}>
        <h4>{t("bankAccountStatement")} <span className="required">*</span></h4>
        <MultiUploadSlot
          label={t("document")}
          sessionId={sessionId}
          docType="BANK_ACCOUNT_STATEMENT"
          partyLocalId={party._localId}
          customerId={customerId}
          documents={documents}
          onUpload={onDocUpload}
          onDelete={onDocDelete}
        />
      </div>
    </div>
  );
}

// ── Main App ──

function App() {
  const { t } = useI18n();
  const noWebsiteReasons = useNoWebsiteReasons();
  const [sessionId] = useState(getSessionId);
  const prefill = useRef(getPrefillData());
  const [form, setForm] = useState<CustomerForm>({ ...EMPTY_CUSTOMER, ...prefill.current });
  const [parties, setParties] = useState<AssociatedParty[]>([]);
  const [documents, setDocuments] = useState<DocEntry[]>([]);
  const [saved, setSaved] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const initialized = useRef(false);
  const lastSavedSnapshot = useRef<string>("");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingDraft = useRef(false);

  // Business doc type selectors
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const sessionMenuRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const applyServerData = (data: any) => {
    const hasInvoice = (data.documents || []).some((d: any) => d.type === "LAST_INVOICE");
    const loadedForm: CustomerForm = {
      ...EMPTY_CUSTOMER,
      ...data,
      jurisdictions: JSON.parse(data.jurisdictions || "[]"),
      involved_activities: JSON.parse(data.involved_activities || "[]"),
      transaction_counterparties: JSON.parse(data.transaction_counterparties || "[]"),
      is_regulated_activity: !!data.is_regulated_activity,
      invoice_type: data.invoice_type || (hasInvoice ? "product" : ""),
    };
    const loadedParties: AssociatedParty[] = (data.associated_parties || []).map((p: any) => ({
      ...makeParty(),
      ...p,
      _localId: p.id || makeLocalId(),
      id_doc_single_side: !!p.id_doc_single_side,
    }));
    const loadedDocs: DocEntry[] = (data.documents || []).map((d: any) => ({
      id: d.id,
      type: d.type,
      side: d.side,
      file_name: d.file_name,
      file_size: d.file_size,
      partyLocalId: d.associated_party_id || d.party_local_id || null,
    }));
    lastSavedSnapshot.current = autosaveSnapshot(loadedForm, loadedParties);
    setForm(loadedForm);
    setParties(loadedParties);
    setDocuments(loadedDocs);
    setSaved(true);
    setSubmitted(loadedForm.status === "SUBMITTED");
  };

  const applyCachedData = (cached: { form: CustomerForm; parties: AssociatedParty[]; documents: DocEntry[] }) => {
    setForm(cached.form);
    setParties(cached.parties);
    setDocuments(cached.documents);
    if (cached.form.id) setSaved(true);
    setSubmitted(cached.form.status === "SUBMITTED");
  };

  const loadFromServer = async (fallbackCached?: { form: CustomerForm; parties: AssociatedParty[]; documents: DocEntry[] }) => {
    try {
      const res = await fetch(`/api/customers/${sessionId}`);
      if (!res.ok) throw new Error("not found");
      applyServerData(await res.json());
      return true;
    } catch {
      if (fallbackCached) applyCachedData(fallbackCached);
      return false;
    }
  };

  const handleSaveConflict = async () => {
    const loaded = await loadFromServer();
    if (loaded) showToast(t("staleFormReloaded"), "error");
  };

  // ── Load from localStorage or DB on mount ──
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const cached = loadSession(sessionId);
    if (cached) {
      const pf = prefill.current;
      const mergedForm = pf ? { ...cached.form } : cached.form;
      if (pf) {
        for (const key of PREFILL_KEYS) {
          if (pf[key] && !mergedForm[key]) (mergedForm as any)[key] = pf[key];
        }
      }
      const mergedCached = { ...cached, form: mergedForm };
      if (mergedForm.id) {
        loadFromServer(mergedCached);
        return;
      }
      applyCachedData(mergedCached);
      return;
    }

    loadFromServer();
  }, [sessionId]);

  // ── Persist to localStorage on every change ──
  useEffect(() => {
    if (!initialized.current) return;
    saveSessionToLS(sessionId, form, parties, documents);
  }, [sessionId, form, parties, documents]);

  useEffect(() => {
    if (!initialized.current || submitted) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      const snapshot = autosaveSnapshot(form, parties);
      if (snapshot === lastSavedSnapshot.current) return;
      if (isSavingDraft.current) return;

      isSavingDraft.current = true;
      try {
        const res = await fetch("/api/customers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer: { ...form, id: form.id || sessionId, status: "DRAFT" },
            parties,
            sessionId,
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as { id: string; partyIdMap: Record<string, string>; revision: number };
          lastSavedSnapshot.current = snapshot;
          setForm(prev => ({ ...prev, id: data.id, revision: data.revision }));
          if (data.partyIdMap) {
            setParties(prev => prev.map(p => {
              const dbId = data.partyIdMap[p._localId];
              return dbId ? { ...p, id: dbId } : p;
            }));
          }
          setSaved(true);
        } else if (res.status === 409) {
          await handleSaveConflict();
        }
      } catch {
        // silently ignore auto-save failures
      } finally {
        isSavingDraft.current = false;
      }
    }, 2000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [form, parties, sessionId, submitted]);

  const set = (key: keyof CustomerForm, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const handleDocUpload = (doc: DocEntry) => setDocuments(prev => [...prev, doc]);

  const handleDocDelete = (id: string) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  };

  const focusField = (fieldId: string) => {
    const el = document.querySelector(`[data-field="${fieldId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = el.querySelector("input, textarea, select") as HTMLElement | null;
    if (input) setTimeout(() => input.focus(), 400);
    el.classList.add("field-error");
    setTimeout(() => el.classList.remove("field-error"), 3000);
  };

  const validateForm = (): { valid: boolean; message?: string; fieldId?: string } => {
    // Company Data
    const requiredFields: [string, keyof CustomerForm, string][] = [
      ["legal_name", "legal_name", t("legalName")],
      ["country", "country", t("country")],
      ["incorporation_date", "incorporation_date", t("incorporationDate")],
      ["email", "email", t("email")],
      ["tax_id", "tax_id", t("taxId")],
      ["phone", "phone", t("phone")],
    ];

    // Website: either website or no_website_reason
    if (!form.website && !form.no_website_reason) {
      return { valid: false, message: t("websiteRequired"), fieldId: "website" };
    }

    for (const [fieldId, key, label] of requiredFields) {
      if (!form[key] || (typeof form[key] === "string" && !(form[key] as string).trim())) {
        return { valid: false, message: `${label} ${t("requiredField")}`, fieldId };
      }
    }

    // Address
    const addressFields: [string, keyof CustomerForm, string][] = [
      ["address_line1", "address_line1", t("addressLine1")],
      ["address_city", "address_city", t("city")],
      ["address_state", "address_state", t("stateProvince")],
      ["address_postal_code", "address_postal_code", t("postalCode")],
      ["address_country", "address_country", t("country")],
    ];
    for (const [fieldId, key, label] of addressFields) {
      if (!form[key] || (typeof form[key] === "string" && !(form[key] as string).trim())) {
        return { valid: false, message: `${label} ${t("requiredField")}`, fieldId };
      }
    }

    // Parties - at least one required
    if (parties.length === 0) {
      return { valid: false, message: t("atLeastOneOwner"), fieldId: "parties_required" };
    }
    if (!parties.some(p => p.role === "UBO")) {
      return { valid: false, message: t("atLeastOneUbo"), fieldId: `party_${parties[0]._localId}_role` };
    }

    // Validate each party
    for (const party of parties) {
      const pId = party._localId;
      if (!party.role) return { valid: false, message: t("ownerRoleRequired"), fieldId: `party_${pId}_role` };
      if (!party.name?.trim()) return { valid: false, message: t("ownerNameRequired"), fieldId: `party_${pId}_name` };
      if (!party.birth_date) return { valid: false, message: t("ownerDobRequired"), fieldId: `party_${pId}_birth_date` };
      if (!party.email?.trim()) return { valid: false, message: t("ownerEmailRequired"), fieldId: `party_${pId}_email` };
      if (!party.tax_id?.trim()) return { valid: false, message: t("ownerTaxIdRequired"), fieldId: `party_${pId}_tax_id` };
      if ((party.role === "UBO" || party.role === "SHAREHOLDER") && !party.ownership_percentage) {
        return { valid: false, message: t("ownerOwnershipRequired"), fieldId: `party_${pId}_ownership` };
      }
      if (!party.address_line1?.trim()) return { valid: false, message: t("ownerAddressRequired"), fieldId: `party_${pId}_address_line1` };
      if (!party.address_city?.trim()) return { valid: false, message: t("ownerCityRequired"), fieldId: `party_${pId}_address_city` };
      if (!party.address_state?.trim()) return { valid: false, message: t("ownerStateRequired"), fieldId: `party_${pId}_address_state` };
      if (!party.address_postal_code?.trim()) return { valid: false, message: t("ownerPostalCodeRequired"), fieldId: `party_${pId}_address_postal_code` };

      // Owner ID document
      const partyDocs = documents.filter(d => d.partyLocalId === pId);
      if (party.id_doc_single_side) {
        const hasId = partyDocs.some(d => d.type === party.id_doc_type && !d.side);
        if (!hasId) return { valid: false, message: t("ownerIdDocRequired"), fieldId: `party_${pId}_id_doc` };
      } else {
        const hasFront = partyDocs.some(d => d.type === party.id_doc_type && d.side === "FRONT_SIDE");
        if (!hasFront) return { valid: false, message: t("ownerIdDocFrontRequired"), fieldId: `party_${pId}_id_doc` };
        const hasBack = partyDocs.some(d => d.type === party.id_doc_type && d.side === "BACK_SIDE");
        if (!hasBack) return { valid: false, message: t("ownerIdDocBackRequired"), fieldId: `party_${pId}_id_doc` };
      }

      // Owner proof of address
      const hasProof = partyDocs.some(d => d.type === "BANK_STATEMENT");
      if (!hasProof) return { valid: false, message: t("ownerProofAddressRequired"), fieldId: `party_${pId}_proof_address` };

      // Owner bank account statement
      const hasBankStatement = partyDocs.some(d => d.type === "BANK_ACCOUNT_STATEMENT");
      if (!hasBankStatement) return { valid: false, message: t("ownerBankAccountStatementRequired"), fieldId: `party_${pId}_bank_account_statement` };
    }

    // Required documents (company-level, no partyLocalId)
    const requiredDocs: [string, string, string][] = [
      ["doc_incorporation", "INCORPORATION_ARTICLES", t("incorporationDocRequired")],
      ["doc_shareholder", "SHAREHOLDER_REGISTRY", t("proofOfOwnershipDocRequired")],
      ["doc_bank_statement", "BANK_STATEMENT", t("proofOfAddressDocRequired")],
      ["doc_corporate_structure", "SIGNED_CORPORATE_STRUCTURE_CHART", t("corporateStructureRequired")],
      ["doc_balance_sheet", "SIGNED_BALANCE_SHEET", t("balanceSheetRequired")],
    ];
    for (const [fieldId, docType, message] of requiredDocs) {
      const hasDoc = documents.some(d => d.type === docType && !d.partyLocalId);
      if (!hasDoc) {
        return { valid: false, message, fieldId };
      }
    }

    // Invoice type is required
    if (!form.invoice_type) {
      return { valid: false, message: t("invoiceTypeRequired"), fieldId: "doc_last_invoice" };
    }

    // Last invoice: product requires invoice; service requires invoice OR contract
    const hasInvoice = documents.some(d => d.type === "LAST_INVOICE" && !d.partyLocalId);
    if (form.invoice_type === "service") {
      const hasContract = documents.some(d => d.type === "INVOICE_CONTRACT" && !d.partyLocalId);
      if (!hasInvoice && !hasContract) {
        return { valid: false, message: t("invoiceOrContractRequired"), fieldId: "doc_last_invoice" };
      }
    } else if (!hasInvoice) {
      return { valid: false, message: t("lastInvoiceRequired"), fieldId: "doc_last_invoice" };
    }

    // Financial
    const financialFields: [string, keyof CustomerForm, string][] = [
      ["company_type", "company_type", t("companyType")],
      ["annual_revenue", "annual_revenue", t("annualRevenue")],
      ["monthly_transaction_volume", "monthly_transaction_volume", t("monthlyTransactionVolume")],
    ];
    for (const [fieldId, key, label] of financialFields) {
      if (!form[key] || (typeof form[key] === "string" && !(form[key] as string).trim())) {
        return { valid: false, message: `${label} ${t("requiredField")}`, fieldId };
      }
    }
    if (form.is_regulated_activity && !form.regulated_activity_details?.trim()) {
      return { valid: false, message: t("regulatedDetailsRequired"), fieldId: "regulated_activity_details" };
    }

    // Source of funds
    if (!form.source_of_funds) return { valid: false, message: t("sourceOfFundsRequired"), fieldId: "source_of_funds" };
    if (!form.monthly_volume_usd?.trim()) return { valid: false, message: t("monthlyVolumeRequired"), fieldId: "monthly_volume_usd" };
    if (!form.transaction_volume_usd?.trim()) return { valid: false, message: t("perTxVolumeRequired"), fieldId: "transaction_volume_usd" };
    if (!form.jurisdictions?.length) return { valid: false, message: t("jurisdictionRequired"), fieldId: "jurisdictions" };
    if (!form.involved_activities?.length) return { valid: false, message: t("activityRequired"), fieldId: "involved_activities" };
    if (!form.transaction_counterparties?.length) return { valid: false, message: t("counterpartyRequired"), fieldId: "transaction_counterparties" };

    return { valid: true };
  };

  const saveCustomer = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      showToast(validation.message!, "error");
      if (validation.fieldId) focusField(validation.fieldId);
      return;
    }

    try {
      const res = await fetch("/api/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: { ...form, id: form.id || sessionId, status: "SUBMITTED" },
          parties,
          sessionId,
        }),
      });
      if (res.status === 409) {
        await handleSaveConflict();
        return;
      }
      if (!res.ok) throw new Error("submit failed");
      const data = (await res.json()) as { id: string; partyIdMap: Record<string, string>; revision: number };
      setForm(prev => ({ ...prev, id: data.id, revision: data.revision, status: "SUBMITTED" }));

      // Update parties with their server-assigned IDs
      if (data.partyIdMap) {
        setParties(prev => prev.map(p => {
          const dbId = data.partyIdMap[p._localId];
          return dbId ? { ...p, id: dbId } : p;
        }));
      }

      setSaved(true);
      setSubmitted(true);
      showToast(t("submittedSuccess"));
    } catch {
      showToast(t("submitFailed"), "error");
    }
  };

  const newSession = () => {
    const newId = crypto.randomUUID().replace(/-/g, "");
    window.location.href = `?session=${newId}`;
  };

  const handleNewSession = () => {
    setSessionMenuOpen(false);
    if (!confirm(t("newSessionConfirm"))) return;
    newSession();
  };

  // Close session menu when clicking outside
  useEffect(() => {
    if (!sessionMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(e.target as Node)) {
        setSessionMenuOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [sessionMenuOpen]);

  const addParty = () => setParties(prev => [...prev, makeParty()]);

  const removeParty = (index: number) => {
    const party = parties[index]!;
    setDocuments(prev => prev.filter(d => d.partyLocalId !== party._localId));
    setParties(prev => prev.filter((_, i) => i !== index));
  };

  const updateParty = (index: number, p: AssociatedParty) => {
    setParties(prev => { const u = [...prev]; u[index] = p; return u; });
  };

  return (
    <>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      {/* Header */}
      <div className="page-header">
        <img src={logoUrl} alt="Vero" className="page-logo" />
        <div className="page-instructions">
          <h1>{t("pageTitle")}</h1>
          {!submitted && <p>{t("pageSubtitle")}</p>}
          <div className="session-menu-wrap instructions-menu" ref={sessionMenuRef}>
            <button
              type="button"
              className="btn btn-icon btn-ghost"
              onClick={() => setSessionMenuOpen(open => !open)}
              aria-label="Session menu"
              aria-expanded={sessionMenuOpen}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="6" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="18" r="1.5" />
              </svg>
            </button>
            {sessionMenuOpen && (
              <div className="session-dropdown">
                <div className="session-dropdown-info">
                  <div className="session-dropdown-label">{t("status")}</div>
                  {submitted ? <span className="submitted-badge">{t("submitted")}</span> : saved ? <span className="saved-badge">{t("changesSaved")}</span> : <span className="draft-badge">{t("draft")}</span>}
                </div>
                <div className="session-dropdown-info">
                  <div className="session-dropdown-label">{t("session")}</div>
                  <code>{sessionId.slice(0, 8)}...{sessionId.slice(-4)}</code>
                </div>
                {form.id && (
                  <div className="session-dropdown-info">
                    <div className="session-dropdown-label">{t("customerId")}</div>
                    <code>{form.id.slice(0, 8)}...{form.id.slice(-4)}</code>
                  </div>
                )}
                <button type="button" className="session-dropdown-item" onClick={handleNewSession}>
                  {t("newSession")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Notes Card (shown when status is REQUEST) */}
      {form.admin_notes && !submitted && (
        <div className="form-section" style={{ paddingBottom: 0 }}>
          <div />
          <div style={{
            padding: "16px 20px",
            background: "#fef9c3", border: "1px solid #facc15", borderRadius: "var(--radius)",
            display: "flex", gap: 12, alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>&#9888;</span>
            <div>
              <strong style={{ fontSize: 14, color: "#92400e" }}>{t("adminNotesCard")}</strong>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#78350f", whiteSpace: "pre-wrap" }}>{form.admin_notes}</p>
            </div>
          </div>
        </div>
      )}

      {submitted ? (
        <div className="submitted-screen">
          <div className="submitted-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h2 className="submitted-title">{t("applicationSubmitted")}</h2>
          <p className="submitted-message">
            {t("submittedMessage")}
          </p>
          <p className="submitted-reference">
            {t("reference")}: <code>{(form.id || sessionId).slice(0, 8)}...{(form.id || sessionId).slice(-4)}</code>
          </p>
          <p className="submitted-note">{t("submittedNote")}</p>
        </div>
      ) : (<>
      {/* ═══════════════ BUSINESS FLOW (KYB) ═══════════════ */}
      <>
          {/* Company Data */}
          <Section title={t("companyData")} description={t("companyDataDesc")}>
            <div className="form-grid">
              <Field label={t("legalName")} required fieldId="legal_name">
                <input value={form.legal_name} onChange={e => set("legal_name", e.target.value)} placeholder={t("phLegalName")} />
              </Field>
              <Field label={t("country")} required fieldId="country">
                <CustomSelect value={form.country} onChange={v => set("country", v)} options={COUNTRIES} placeholder={t("selectCountry")} />
              </Field>
              <Field label={t("doingBusinessAs")}>
                <input value={form.services_provided} onChange={e => set("services_provided", e.target.value)} placeholder={t("phDoingBusinessAs")} />
              </Field>
              <Field label={t("incorporationDate")} required fieldId="incorporation_date">
                <input type="date" value={form.incorporation_date} onChange={e => set("incorporation_date", e.target.value)} />
              </Field>
              <Field label={t("email")} required fieldId="email">
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder={t("phEmail")} />
              </Field>
              <Field label={t("taxId")} required fieldId="tax_id">
                <input value={form.tax_id} onChange={e => set("tax_id", e.target.value)} placeholder={t("phTaxId")} />
              </Field>
              <Field label={t("phone")} required fieldId="phone">
                <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder={t("phPhone")} />
              </Field>
              <Field label={t("website")} required fullWidth fieldId="website">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {!form.no_website_reason ? (
                    <input style={{ flex: 1 }} value={form.website} onChange={e => set("website", e.target.value)} placeholder={t("phWebsite")} />
                  ) : (
                    <CustomSelect
                      value={form.no_website_reason}
                      onChange={v => set("no_website_reason", v)}
                      options={noWebsiteReasons}
                      placeholder={t("selectReason")}
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      style={{ width: 16, height: 16, margin: 0, flexShrink: 0, padding: 0, border: "none", boxShadow: "none" }}
                      checked={!!form.no_website_reason}
                      onChange={e => {
                        if (e.target.checked) {
                          setForm(prev => ({ ...prev, website: "", no_website_reason: "UNDER_DEVELOPMENT" }));
                        } else {
                          setForm(prev => ({ ...prev, no_website_reason: "" }));
                        }
                      }}
                    />
                    {t("noWebsite")}
                  </label>
                </div>
              </Field>
            </div>
          </Section>

          {/* Physical Address */}
          <Section title={t("physicalAddress")} description={t("physicalAddressDesc")}>
            <div className="form-grid">
              <Field label={t("addressLine1")} required fieldId="address_line1">
                <input value={form.address_line1} onChange={e => set("address_line1", e.target.value)} placeholder={t("phAddress")} />
              </Field>
              <Field label={t("city")} required fieldId="address_city">
                <input value={form.address_city} onChange={e => set("address_city", e.target.value)} placeholder={t("phCity")} />
              </Field>
              <Field label={t("stateProvince")} required fieldId="address_state">
                <input value={form.address_state} onChange={e => set("address_state", e.target.value)} placeholder={t("phState")} />
              </Field>
              <Field label={t("postalCode")} required fieldId="address_postal_code">
                <input value={form.address_postal_code} onChange={e => set("address_postal_code", e.target.value)} placeholder={t("phPostalCode")} />
              </Field>
              <Field label={t("country")} required fieldId="address_country">
                <CustomSelect value={form.address_country} onChange={v => set("address_country", v)} options={COUNTRIES} placeholder={t("selectCountry")} />
              </Field>
            </div>
          </Section>

          {/* List of Business Owners */}
          <Section title={t("businessOwners")} description={t("businessOwnersDesc")}>
            {parties.length === 0 && <div data-field="parties_required" />}
            {parties.map((party, i) => (
              <OwnerCard
                key={party._localId}
                party={party}
                index={i}
                onChange={p => updateParty(i, p)}
                onRemove={() => removeParty(i)}
                sessionId={sessionId}
                documents={documents}
                onDocUpload={handleDocUpload}
                onDocDelete={handleDocDelete}
                customerId={form.id}
              />
            ))}
            <button className="btn-add-owner" onClick={addParty}>{t("addOwner")}</button>
          </Section>

          {/* Documents (Company-level) */}
          <Section title={t("documents")} description={t("documentsDesc")}>
            {/* Incorporation */}
            <div className="doc-subsection" data-field="doc_incorporation">
              <h4>{t("incorporation")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("incorporationDesc")}</p>
              <UploadSlot
                label={t("document")}
                sessionId={sessionId}
                docType="INCORPORATION_ARTICLES"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>

            {/* Proof of Ownership */}
            <div className="doc-subsection" data-field="doc_shareholder">
              <h4>{t("proofOfOwnership")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("proofOfOwnershipDesc")}</p>
              <UploadSlot
                label={t("document")}
                sessionId={sessionId}
                docType="SHAREHOLDER_REGISTRY"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>

            {/* Proof of Address */}
            <div className="doc-subsection" data-field="doc_bank_statement">
              <h4>{t("proofOfAddressDoc")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("proofOfAddressDocDesc")}</p>
              <UploadSlot
                label={t("document")}
                sessionId={sessionId}
                docType="BANK_STATEMENT"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>

            {/* Corporate Structure Chart */}
            <div className="doc-subsection" data-field="doc_corporate_structure">
              <h4>{t("corporateStructureChart")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("corporateStructureChartDesc")}</p>
              <UploadSlot
                label={t("document")}
                sessionId={sessionId}
                docType="SIGNED_CORPORATE_STRUCTURE_CHART"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>

            {/* Signed Balance Sheet */}
            <div className="doc-subsection" data-field="doc_balance_sheet">
              <h4>{t("signedBalanceSheet")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("signedBalanceSheetDesc")}</p>
              <UploadSlot
                label={t("document")}
                sessionId={sessionId}
                docType="SIGNED_BALANCE_SHEET"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>

            {/* Last Invoice */}
            <div className="doc-subsection" data-field="doc_last_invoice">
              <h4>{t("lastInvoice")} <span className="required">*</span></h4>
              <p className="doc-desc">{t("lastInvoiceDesc")}</p>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">{t("invoiceType")} <span className="required">*</span></label>
                <select className="form-select" value={form.invoice_type} onChange={e => setForm(f => ({ ...f, invoice_type: e.target.value }))}>
                  <option value="">{t("selectInvoiceType")}</option>
                  <option value="product">{t("invoiceTypeProduct")}</option>
                  <option value="service">{t("invoiceTypeService")}</option>
                </select>
              </div>

              <UploadSlot
                label={t("invoiceDocument")}
                description={t("invoiceDocumentDesc")}
                sessionId={sessionId}
                docType="LAST_INVOICE"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
                disabled={!form.invoice_type}
              />
              {form.invoice_type === "service" && (
                <UploadSlot
                  label={t("invoiceContract")}
                  description={t("invoiceContractDesc")}
                  sessionId={sessionId}
                  docType="INVOICE_CONTRACT"
                  customerId={form.id}
                  documents={documents}
                  onUpload={handleDocUpload}
                  onDelete={handleDocDelete}
                />
              )}
            </div>

            {/* Regulated Activity Document (conditional) */}
            {form.is_regulated_activity && (
              <div className="doc-subsection">
                <h4>{t("regulatedActivityDocument")}</h4>
                <p className="doc-desc">{t("regulatedActivityDocDesc")}</p>
                <UploadSlot
                  label={t("document")}
                  sessionId={sessionId}
                  docType="REGULATED_ACTIVITY_DOCUMENT"
                  customerId={form.id}
                  documents={documents}
                  onUpload={handleDocUpload}
                  onDelete={handleDocDelete}
                />
              </div>
            )}

            {/* Optional docs */}
            <div className="doc-subsection">
              <h4>{t("additionalDocuments")}</h4>
              <p className="doc-desc">{t("additionalDocumentsDesc")}</p>
              <MultiUploadSlot
                label={t("signedIncomeStatement")}
                sessionId={sessionId}
                docType="SIGNED_INCOME_STATEMENT"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
              <MultiUploadSlot
                label={t("powerOfAttorney")}
                sessionId={sessionId}
                docType="POWER_OF_ATTORNEY"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
              <MultiUploadSlot
                label={t("other")}
                sessionId={sessionId}
                docType="OTHER"
                customerId={form.id}
                documents={documents}
                onUpload={handleDocUpload}
                onDelete={handleDocDelete}
              />
            </div>
          </Section>

          {/* Financial Information */}
          <Section title={t("financialInformation")} description={t("financialInformationDesc")}>
            <div className="form-grid">
              <Field label={t("companyType")} required fullWidth fieldId="company_type">
                <CustomSelect
                  value={form.company_type}
                  onChange={v => set("company_type", v)}
                  options={COMPANY_TYPES}
                  placeholder={t("select")}
                />
              </Field>
              <Field label={t("businessDescription")} fullWidth>
                <textarea value={form.services_provided} onChange={e => set("services_provided", e.target.value)} placeholder={t("phBusinessDesc")} />
              </Field>
              <Field label={t("annualRevenue")} required fieldId="annual_revenue">
                <input value={form.annual_revenue} onChange={e => set("annual_revenue", e.target.value)} placeholder={t("phAnnualRevenue")} />
              </Field>
              <Field label={t("monthlyTransactionVolume")} required fieldId="monthly_transaction_volume">
                <input value={form.monthly_transaction_volume} onChange={e => set("monthly_transaction_volume", e.target.value)} placeholder={t("phMonthlyTxVolume")} />
              </Field>
              <Field label={t("isRegulatedActivity")} required fullWidth fieldId="is_regulated_activity">
                <CustomSelect
                  value={form.is_regulated_activity ? "true" : "false"}
                  onChange={v => set("is_regulated_activity", v === "true")}
                  options={[
                    { value: "false", label: t("no") },
                    { value: "true", label: t("yes") },
                  ]}
                />
              </Field>
              {form.is_regulated_activity && (
                <Field label={t("regulatedActivityDetails")} required fieldId="regulated_activity_details">
                  <input value={form.regulated_activity_details} onChange={e => set("regulated_activity_details", e.target.value)} />
                </Field>
              )}
            </div>
          </Section>

          {/* Source of Funds */}
          <Section title={t("sourceOfFunds")} description={t("sourceOfFundsDesc")}>
            <div className="form-grid">
              <Field label={t("sourceOfFunds")} required fullWidth fieldId="source_of_funds">
                <CustomSelect
                  value={form.source_of_funds}
                  onChange={v => set("source_of_funds", v)}
                  options={SOURCE_OF_FUNDS_BUSINESS}
                  placeholder={t("select")}
                />
              </Field>
              <Field label={t("monthlyVolumeUsd")} required fieldId="monthly_volume_usd">
                <input value={form.monthly_volume_usd} onChange={e => set("monthly_volume_usd", e.target.value)} placeholder={t("phMonthlyVolume")} />
              </Field>
              <Field label={t("perTransactionVolumeUsd")} required fieldId="transaction_volume_usd">
                <input value={form.transaction_volume_usd} onChange={e => set("transaction_volume_usd", e.target.value)} placeholder={t("phPerTxVolume")} />
              </Field>
            </div>
            <div data-field="jurisdictions">
              <div className="upload-slot-label" style={{ marginBottom: 8 }}>{t("jurisdictions")}<span className="required">*</span></div>
              <CheckboxGroup options={JURISDICTIONS} value={form.jurisdictions} onChange={v => set("jurisdictions", v)} />
            </div>
            <div data-field="involved_activities">
              <div className="upload-slot-label" style={{ marginBottom: 8 }}>{t("involvedActivities")}<span className="required">*</span></div>
              <CheckboxGroup options={INVOLVED_ACTIVITIES} value={form.involved_activities} onChange={v => set("involved_activities", v)} />
            </div>
            <div data-field="transaction_counterparties">
              <div className="upload-slot-label" style={{ marginBottom: 8 }}>{t("transactionCounterparties")}<span className="required">*</span></div>
              <CheckboxGroup options={TRANSACTION_COUNTERPARTIES} value={form.transaction_counterparties} onChange={v => set("transaction_counterparties", v)} />
            </div>
          </Section>

      </>

      {/* Confirm / Submit */}
      <div className="submit-bar">
        <button className="btn btn-primary" onClick={saveCustomer}>
          {saved ? t("update") : t("submitApplication")}
        </button>
      </div>
      </>)}

      <LanguageSwitcher />
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <I18nProvider>
    <App />
  </I18nProvider>
);
